import React, { useState, useEffect, useRef } from "react";
import { Grid, Input, Icon, Button, Modal, Label } from "semantic-ui-react";
import "semantic-ui-css/semantic.min.css";
import "../../styles/scss/unpaidInvoiceList.scss";
import { useLoginCheck } from "../../hooks/useLoginCheck";

const DEFAULT_SUBJECT = "未払い料金のお支払いについて";
const DEFAULT_BODY = `{name}さま

いつもお世話になっております。
SLASH DANCE STUDIOです。

現在、月謝または年会費のお支払いが確認できておりません。
内容をご確認のうえ、速やかにお支払いをお願いいたします。

月謝の支払い期限は毎月7日となっております。

■お支払い方法
マイページの「支払い履歴」よりお支払いいただけます。
未払いと表示されている部分をタップでお支払い画面が表示されます。
https://app.dance-slash.com/my-payments

■自動引落の設定
お支払い忘れ防止のため、自動引落の設定も可能です。
すでに発行されている請求書は自動引落となりませんので、マイページから手動でのお支払いをお願いいたします。
https://app.dance-slash.com/payment-method

【注意事項】
料金の支払いが確認できない場合は、レッスンの受講制限等の対応を行う場合がございますので、必ずお支払いをお願いいたします。

※すでにお支払い済みの場合は、本メールと行き違いとなりますことをご容赦ください。

ご不明な点がございましたら、下記公式LINEまでお問い合わせください。

円滑な運営のため、ご理解とご協力のほどよろしくお願いいたします。

―――――――――――――――――――――――――――
SLASH DANCE STUDIO（スラッシュダンススタジオ）
LINE：https://line.me/R/ti/p/%40667pieed
URL：https://dance-slash.com/
―――――――――――――――――――――――――――
`;

function UnpaidInvoiceListPage() {
  useLoginCheck();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [loading, setLoading] = useState(true);

  // メール送信モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [mailSubject, setMailSubject] = useState(DEFAULT_SUBJECT);
  const [mailBody, setMailBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(false);

  // 強制引落モーダル
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [charging, setCharging] = useState(false);

  // 強制引落結果モーダル
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [chargeResults, setChargeResults] = useState(null);

  // メモ
  const [memoValues, setMemoValues] = useState({});
  const [savingMemo, setSavingMemo] = useState({});

  const searchInputRef = useRef(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_unpaid_invoices/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setCustomers(data);
      setFilteredCustomers(data);
      const memos = {};
      data.forEach((c) => { memos[c.customer_id] = c.memo || ""; });
      setMemoValues(memos);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedCustomers = React.useMemo(() => {
    let sortable = [...filteredCustomers];
    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === "unpaid_count") {
          aValue = a.invoices.length;
          bValue = b.invoices.length;
        }
        if (sortConfig.key === "late_fee_total") {
          aValue = (a.late_fee_total_preview && a.late_fee_total_preview.total_amount) || 0;
          bValue = (b.late_fee_total_preview && b.late_fee_total_preview.total_amount) || 0;
        }
        // null値は常に末尾
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        if (aValue < bValue)
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (aValue > bValue)
          return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredCustomers, sortConfig]);

  const handleSearch = (event) => {
    if (event.type === "click" || event.key === "Enter") {
      const keyword = searchKeyword;
      const filtered = customers.filter((customer) => {
        return (
          (customer.first_name && customer.first_name.includes(keyword)) ||
          (customer.last_name && customer.last_name.includes(keyword)) ||
          (customer.first_name_yomi &&
            customer.first_name_yomi.includes(keyword)) ||
          (customer.last_name_yomi &&
            customer.last_name_yomi.includes(keyword)) ||
          (customer.customer_id &&
            customer.customer_id.toString().includes(keyword)) ||
          (customer.mail && customer.mail.includes(keyword)) ||
          (customer.phone && customer.phone.includes(keyword)) ||
          (customer.last_reminder_date && customer.last_reminder_date.includes(keyword))
        );
      });
      setFilteredCustomers(filtered);
    }
  };

  // 支払い方法は 2 択: card_auto / invoice。旧 card_invoice / bank_transfer も
  // 「請求書払い」へマップ（履歴互換）。
  const paymentMethodLabel = (method) => {
    if (method === "card_auto") return "自動引落";
    return "請求書払い";
  };

  const openInvoiceUrl = async (invoiceId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_invoice_url/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ invoice_id: invoiceId }),
      });
      if (!response.ok) {
        throw new Error("URLの取得に失敗しました。");
      }
      const data = await response.json();
      window.open(data.hosted_invoice_url, "_blank");
    } catch (error) {
      console.error(error);
      alert("請求書URLの取得に失敗しました。");
    }
  };

  const handleOpenModal = () => {
    setMailSubject(DEFAULT_SUBJECT);
    setMailBody(DEFAULT_BODY);
    setModalOpen(true);
  };

  const handleSendReminder = async () => {
    setSending(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const customerIds = filteredCustomers.map((c) => c.customer_id);
      const response = await fetch(`${apiUrl}/api/send_unpaid_reminder/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customer_ids: customerIds,
          subject: mailSubject,
          body: mailBody,
        }),
      });
      if (!response.ok) {
        throw new Error("送信に失敗しました。");
      }
      const data = await response.json();
      setModalOpen(false);
      alert(
        `${data.sent_count}件のメールを送信しました。` +
          (data.errors.length > 0
            ? `\n${data.errors.length}件の送信エラーがありました。`
            : "")
      );
      fetchData();
    } catch (error) {
      console.error(error);
      alert("メール送信に失敗しました。");
    } finally {
      setSending(false);
    }
  };

  // 強制引落対象の顧客（受講実績ありの請求書が1件以上ある顧客）
  const eligibleCustomers = filteredCustomers.filter(
    (c) => c.force_charge_eligible
  );
  const ineligibleCustomers = filteredCustomers.filter(
    (c) => !c.force_charge_eligible
  );

  const handleOpenChargeModal = () => {
    setChargeModalOpen(true);
  };

  const handleForceCharge = async () => {
    setCharging(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const customerIds = eligibleCustomers.map((c) => c.customer_id);
      const response = await fetch(`${apiUrl}/api/force_charge_unpaid/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ customer_ids: customerIds }),
      });
      if (!response.ok) {
        throw new Error("強制引落に失敗しました。");
      }
      const data = await response.json();
      setChargeModalOpen(false);
      setChargeResults(data);
      setResultModalOpen(true);
    } catch (error) {
      console.error(error);
      alert("強制引落の実行に失敗しました。");
    } finally {
      setCharging(false);
    }
  };

  const handleCloseResultModal = () => {
    setResultModalOpen(false);
    setChargeResults(null);
    fetchData();
  };

  const handleSaveMemo = async (customerId) => {
    setSavingMemo((prev) => ({ ...prev, [customerId]: true }));
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/save_unpaid_memo/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          memo: memoValues[customerId] || "",
        }),
      });
      if (!response.ok) throw new Error("保存に失敗しました。");
      // customers state のメモも更新
      const updateMemo = (list) =>
        list.map((c) =>
          c.customer_id === customerId
            ? { ...c, memo: memoValues[customerId] || "" }
            : c
        );
      setCustomers(updateMemo);
      setFilteredCustomers(updateMemo);
    } catch (error) {
      console.error(error);
      alert("メモの保存に失敗しました。");
    } finally {
      setSavingMemo((prev) => ({ ...prev, [customerId]: false }));
    }
  };

  // 請求書ごとの直近エラーラベル
  const getChargeStatusLabel = (chargeLog) => {
    if (!chargeLog) return null;
    if (chargeLog.status === "failed") {
      return (
        <Label color="red" size="tiny" className="charge-status-label">
          <Icon name="warning sign" />
          {chargeLog.error_message}
        </Label>
      );
    }
    if (chargeLog.status === "skipped") {
      return (
        <Label color="grey" size="tiny" className="charge-status-label">
          {chargeLog.error_message}
        </Label>
      );
    }
    return null;
  };

  const SortIcon = ({ columnKey }) => (
    <>
      {sortConfig.key === columnKey &&
        sortConfig.direction === "ascending" && <Icon name="triangle up" />}
      {sortConfig.key === columnKey &&
        sortConfig.direction === "descending" && <Icon name="triangle down" />}
    </>
  );

  const totalInvoices = customers.reduce(
    (acc, c) => acc + c.invoices.length,
    0
  );

  if (loading) {
    return (
      <div className="page-loading">
        <div className="page-loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Grid>
        <h1>未払い管理</h1>
        <Input
          icon={{
            name: "search",
            circular: true,
            link: true,
            onClick: handleSearch,
          }}
          placeholder="Search..."
          value={searchKeyword}
          ref={searchInputRef}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyDown={handleSearch}
        />
        <p>
          未払い者: {customers.length}人 / 未払い件数: {totalInvoices}件
        </p>
        <div className="send-btn-wrapper">
          <Button
            color="orange"
            onClick={handleOpenChargeModal}
            disabled={eligibleCustomers.length === 0}
          >
            <Icon name="credit card" />
            強制引落（{eligibleCustomers.length}人）
          </Button>
          <Button
            color="red"
            onClick={handleOpenModal}
            disabled={filteredCustomers.length === 0}
          >
            <Icon name="mail" />
            催促メールを送信（{filteredCustomers.length}人）
          </Button>
        </div>
      </Grid>

      <Grid>
        <Grid.Column width={16}>
          <ul className="customer-grid customer-header">
            <li
              className="cell"
              onClick={() => handleSort("customer_id")}
            >
              顧客ID
              <SortIcon columnKey="customer_id" />
            </li>
            <li
              className="cell"
              onClick={() => handleSort("first_name_yomi")}
            >
              名前
              <SortIcon columnKey="first_name_yomi" />
            </li>
            <li className="cell" onClick={() => handleSort("mail")}>
              メール
              <SortIcon columnKey="mail" />
            </li>
            <li className="cell" onClick={() => handleSort("phone")}>
              電話番号
              <SortIcon columnKey="phone" />
            </li>
            <li
              className="cell"
              onClick={() => handleSort("payment_method")}
            >
              支払方法
              <SortIcon columnKey="payment_method" />
            </li>
            <li
              className="cell"
              onClick={() => handleSort("unpaid_count")}
            >
              未払い件数
              <SortIcon columnKey="unpaid_count" />
            </li>
            <li
              className="cell"
              onClick={() => handleSort("late_fee_total")}
            >
              延滞見込額
              <SortIcon columnKey="late_fee_total" />
            </li>
            <li
              className="cell"
              onClick={() => handleSort("last_reminder_date")}
            >
              最終催促日
              <SortIcon columnKey="last_reminder_date" />
            </li>
            <li className="cell">引落対象</li>
            <li className="cell">詳細</li>
          </ul>

          {sortedCustomers.map((customer) => (
            <React.Fragment key={customer.customer_id}>
              <ul className="customer-grid">
                <li className="cell">{customer.customer_id}</li>
                <li className="cell">
                  {customer.first_name} {customer.last_name}
                  <span className="yomi">
                    {customer.first_name_yomi} {customer.last_name_yomi}
                  </span>
                  {customer.memo ? (
                    <Icon name="sticky note" color="yellow" className="memo-icon has-memo" title={customer.memo} />
                  ) : (
                    <Icon name="sticky note outline" className="memo-icon no-memo" />
                  )}
                </li>
                <li className="cell">{customer.mail}</li>
                <li className="cell">{customer.phone}</li>
                <li className="cell">
                  {paymentMethodLabel(customer.payment_method)}
                </li>
                <li className="cell">{customer.invoices.length}件</li>
                <li className="cell late-fee-total-cell">
                  {customer.late_fee_total_preview &&
                  customer.late_fee_total_preview.total_amount > 0 ? (
                    <span
                      className="late-fee-badge"
                      title={`延滞金 ¥${customer.late_fee_total_preview.total_late_fee.toLocaleString()} + 事務手数料 ¥${customer.late_fee_total_preview.admin_fee}`}
                    >
                      +¥{customer.late_fee_total_preview.total_amount.toLocaleString()}
                    </span>
                  ) : (
                    <span className="no-late-fee">-</span>
                  )}
                </li>
                <li className="cell">
                  {customer.last_reminder_date || "-"}
                </li>
                <li className="cell">
                  {customer.force_charge_eligible ? (
                    <Label color="blue" size="tiny">対象</Label>
                  ) : (
                    <Label color="grey" size="tiny">対象外</Label>
                  )}
                </li>
                <li className="cell">
                  <span
                    className="toggle-btn"
                    onClick={() =>
                      setOpenCustomerId(
                        openCustomerId === customer.customer_id
                          ? null
                          : customer.customer_id
                      )
                    }
                  >
                    <Icon
                      name={
                        openCustomerId === customer.customer_id
                          ? "chevron up"
                          : "chevron down"
                      }
                    />
                  </span>
                </li>
              </ul>

              <div
                className="invoice-detail"
                style={{
                  maxHeight:
                    openCustomerId === customer.customer_id
                      ? `${customer.invoices.length * 70 + 140}px`
                      : "0px",
                }}
              >
                <div className="memo-section">
                  <div className="memo-label">
                    <Icon name="sticky note outline" />
                    メモ
                  </div>
                  <div className="memo-input-wrapper">
                    <textarea
                      className="memo-input"
                      placeholder="メモを入力..."
                      value={memoValues[customer.customer_id] || ""}
                      onChange={(e) =>
                        setMemoValues((prev) => ({
                          ...prev,
                          [customer.customer_id]: e.target.value,
                        }))
                      }
                      rows={2}
                    />
                    <Button
                      size="mini"
                      color="blue"
                      className="memo-save-btn"
                      loading={savingMemo[customer.customer_id]}
                      disabled={savingMemo[customer.customer_id] || memoValues[customer.customer_id] === (customer.memo || "")}
                      onClick={() => handleSaveMemo(customer.customer_id)}
                    >
                      保存
                    </Button>
                  </div>
                </div>
                <ul className="invoice-grid invoice-header">
                  <li className="cell">請求日</li>
                  <li className="cell">商品名</li>
                  <li className="cell">金額</li>
                  <li className="cell">延滞金</li>
                  <li className="cell">受講</li>
                  <li className="cell">引落状況</li>
                  <li className="cell">請求書</li>
                </ul>
                {customer.invoices.map((invoice) => (
                  <ul key={invoice.invoice_id} className="invoice-grid">
                    <li className="cell">{invoice.invoice_date}</li>
                    <li className="cell">{invoice.product_name}</li>
                    <li className="cell">
                      {Number(invoice.amount).toLocaleString()}円
                    </li>
                    <li className="cell late-fee-cell">
                      {invoice.late_fee_preview &&
                      invoice.late_fee_preview.late_days > 0 ? (
                        <span className="late-fee-sub">
                          延滞 {invoice.late_fee_preview.late_days}日 / +¥{invoice.late_fee_preview.late_fee_amount}
                        </span>
                      ) : (
                        <span className="no-late-fee">-</span>
                      )}
                    </li>
                    <li className="cell">
                      {invoice.is_annual_fee ? (
                        "-"
                      ) : invoice.has_attendance ? (
                        <Icon name="check circle" color="green" />
                      ) : (
                        <Icon name="remove circle" color="grey" />
                      )}
                    </li>
                    <li className="cell charge-status-cell">
                      {getChargeStatusLabel(invoice.charge_log)}
                    </li>
                    <li className="cell">
                      <span
                        className="invoice-link"
                        onClick={() => openInvoiceUrl(invoice.invoice_id)}
                      >
                        <Icon name="external alternate" />
                        Stripe
                      </span>
                    </li>
                  </ul>
                ))}
              </div>
            </React.Fragment>
          ))}
        </Grid.Column>
      </Grid>

      {/* 催促メール送信モーダル */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="large"
        className="reminder-modal"
      >
        <Modal.Header>催促メール送信確認</Modal.Header>
        <Modal.Content scrolling>
          <div className="modal-section">
            <h4>送信先（{filteredCustomers.length}人）</h4>
            <div className="recipient-list">
              {filteredCustomers.map((c) => (
                <div key={c.customer_id} className="recipient-item">
                  <span className="recipient-name">
                    {c.first_name} {c.last_name}
                  </span>
                  <span className="recipient-mail">{c.mail}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-section">
            <h4>件名</h4>
            <Input
              fluid
              value={mailSubject}
              onChange={(e) => setMailSubject(e.target.value)}
            />
          </div>

          <div className="modal-section">
            <h4>本文</h4>
            <p className="body-hint">
              ※ {"{name}"}には自動的に送信相手の苗字が入ります。
            </p>
            <textarea
              className="mail-body-input"
              value={mailBody}
              onChange={(e) => setMailBody(e.target.value)}
              rows={12}
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)} disabled={sending}>
            キャンセル
          </Button>
          <Button
            color="red"
            onClick={handleSendReminder}
            loading={sending}
            disabled={sending}
          >
            <Icon name="mail" />
            {filteredCustomers.length}人に送信
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 強制引落確認モーダル */}
      <Modal
        open={chargeModalOpen}
        onClose={() => setChargeModalOpen(false)}
        size="large"
        className="charge-modal"
      >
        <Modal.Header>強制引落確認</Modal.Header>
        <Modal.Content scrolling>
          <div className="modal-section">
            <h4>
              <Icon name="check circle" color="blue" />
              引落対象（{eligibleCustomers.length}人）
            </h4>
            <p className="charge-hint">
              以下の顧客の未払い請求書に対して、登録されているカードで強制引落を実行します。
            </p>
            <div className="recipient-list">
              {eligibleCustomers.map((c) => (
                <div key={c.customer_id} className="recipient-item">
                  <span className="recipient-name">
                    {c.first_name} {c.last_name}
                  </span>
                  <span className="recipient-detail">
                    {paymentMethodLabel(c.payment_method)} / {c.invoices.length}件 /
                    合計 {c.invoices.reduce((sum, inv) => sum + Number(inv.amount), 0).toLocaleString()}円
                  </span>
                </div>
              ))}
            </div>
          </div>

          {ineligibleCustomers.length > 0 && (
            <div className="modal-section">
              <h4>
                <Icon name="remove circle" color="grey" />
                対象外（{ineligibleCustomers.length}人）- 受講実績なしの請求書あり
              </h4>
              <div className="recipient-list ineligible-list">
                {ineligibleCustomers.map((c) => (
                  <div key={c.customer_id} className="recipient-item">
                    <span className="recipient-name ineligible">
                      {c.first_name} {c.last_name}
                    </span>
                    <span className="recipient-detail">
                      受講なしの請求書あり
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setChargeModalOpen(false)} disabled={charging}>
            キャンセル
          </Button>
          <Button
            color="orange"
            onClick={handleForceCharge}
            loading={charging}
            disabled={charging || eligibleCustomers.length === 0}
          >
            <Icon name="credit card" />
            {eligibleCustomers.length}人に強制引落を実行
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 強制引落結果モーダル */}
      <Modal
        open={resultModalOpen}
        onClose={handleCloseResultModal}
        size="large"
        className="result-modal"
      >
        <Modal.Header>強制引落結果</Modal.Header>
        <Modal.Content scrolling>
          {chargeResults && (
            <>
              <div className="result-summary">
                <Label color="green" size="large">
                  <Icon name="check" />
                  成功: {chargeResults.summary.success_count}件
                </Label>
                <Label color="red" size="large">
                  <Icon name="warning sign" />
                  失敗: {chargeResults.summary.failed_count}件
                </Label>
                <Label color="grey" size="large">
                  <Icon name="minus" />
                  スキップ: {chargeResults.summary.skipped_count}件
                </Label>
              </div>

              <div className="result-detail-list">
                {chargeResults.results.map((result) => (
                  <div key={result.customer_id} className="result-customer">
                    <div className="result-customer-name">
                      {result.customer_name}（ID: {result.customer_id}）
                    </div>
                    {result.invoices.map((inv) => (
                      <div
                        key={inv.invoice_id}
                        className={`result-invoice result-${inv.status}`}
                      >
                        <span className="result-invoice-id">
                          {inv.invoice_id}
                        </span>
                        {inv.status === "success" && (
                          <Label color="green" size="tiny">
                            <Icon name="check" /> 成功
                          </Label>
                        )}
                        {inv.status === "failed" && (
                          <Label color="red" size="tiny">
                            <Icon name="warning sign" /> {inv.error_message}
                          </Label>
                        )}
                        {inv.status === "skipped" && (
                          <Label color="grey" size="tiny">
                            {inv.error_message}
                          </Label>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="blue" onClick={handleCloseResultModal}>
            閉じる
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default UnpaidInvoiceListPage;
